import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getRecaptchaToken } from '../utils/recaptcha';
import api from '../services/api';
import axios from 'axios';
import DiasporaOnboarding from '../components/onboarding/DiasporaOnboarding';
import UnifiedOnboardingModal from '../components/onboarding/UnifiedOnboardingModal';
import ImprovedRoleSelector from '../components/auth/ImprovedRoleSelector';
import { useFeatures } from '../contexts/FeaturesContext';
import ProjectManagerOnboarding from '../components/onboarding/ProjectManagerOnboarding';
import {
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  LinearProgress,
  Checkbox
} from '@mui/material';
import PhoneField, { isCompletePhone } from '../components/common/PhoneField';
import {
  Person,
  Email,
  Phone,
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircle,
  MarkEmailRead,
  Home,
  Engineering,
  VerifiedUser,
  ArrowForward,
  ArrowBack,
  LocationOn,
  Business
} from '@mui/icons-material';
import { brand, status, text, bg, border, shadow } from '../theme/colors';

// Step-specific images and context for the left panel
const STEP_IMAGES = [
  { src: '/images/register-owner.png', quote: 'Join 500+ verified professionals', stat: '500+ Contractors' },
  { src: '/images/register-contractor.png', quote: 'Building dreams across Zimbabwe', stat: '150+ Verifiers' },
  { src: '/images/register-contact.png', quote: 'Manage everything from your phone', stat: '$10M+ Protected' },
  { src: '/images/register-secure.png', quote: 'Your project, protected', stat: '99.2% Accuracy' },
];

// Role-specific imagery for the "Your Details" step (step index 1), so the
// panel reflects who is actually signing up rather than a generic photo.
const ROLE_DETAIL_IMAGES = {
  client: { src: '/images/register-owner.png', quote: 'Build with total peace of mind', stat: '500+ Verified Pros' },
  contractor: { src: '/images/register-contractor.png', quote: 'Building dreams across Zimbabwe', stat: 'Find your next project' },
  verifier: { src: '/images/inspector-quality.png', quote: 'Be the trusted eyes on the ground', stat: 'Paid per completed verification' },
};

// Role definitions with icons and descriptions
const ROLE_OPTIONS = [
  {
    value: 'client',
    title: 'I Need Work Done',
    description: 'Hire verified contractors, track progress, and pay only for completed work',
    icon: Home,
    color: brand.primary,
    features: ['Payment Protection', 'Verified Contractors', 'Progress Tracking']
  },
  {
    value: 'contractor',
    title: 'I Do The Work',
    description: 'Get hired by property owners, manage projects, and get paid per milestone',
    icon: Engineering,
    color: brand.secondary,
    features: ['Find Projects', 'Milestone Payments', 'Build Reputation']
  },
  {
    value: 'verifier',
    title: 'I Verify Quality',
    description: 'Inspect construction work independently and earn verification fees',
    icon: VerifiedUser,
    color: brand.accent,
    features: ['Flexible Schedule', 'Competitive Fees', 'Professional Network']
  }
];

const steps = ['Select Role', 'Your Details', 'Contact Info', 'Secure Account'];

const RegisterPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [signupStatus, setSignupStatus] = useState({}); // role -> { isOpen, pausedMessage }
  const features = useFeatures();
  const [roleClosedMessage, setRoleClosedMessage] = useState(''); // shown when selected role is paused
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    role: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country_of_residence: '',
    city: '',
    province: '',
    district: '',
    password: '',
    confirmPassword: '',
    account_type: 'individual',
    company_name: '',
    registration_number: '',
    accepted_terms: false
  });
  
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showDiasporaOnboarding, setShowDiasporaOnboarding] = useState(false);
  const [showConversationalOnboarding, setShowConversationalOnboarding] = useState(false);
  const [onboardingPhase, setOnboardingPhase] = useState(1); // 1: Registration, 2: AI Profile, 3: Full Profile
  const [showProjectManagerOnboarding, setShowProjectManagerOnboarding] = useState(false);
  const [showVerifierOnboarding, setShowVerifierOnboarding] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [registeredUser, setRegisteredUser] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const verificationCodeRef = useRef(''); // keeps latest value for auto-submit
  const [verificationError, setVerificationError] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0); // seconds remaining
  const [resendSuccess, setResendSuccess] = useState('');
  const [emailSentWarning, setEmailSentWarning] = useState(false);
  const [codeExpirySeconds, setCodeExpirySeconds] = useState(0);
  const digitRefs = useRef([]);
  const [inviteData, setInviteData] = useState(null);
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, verifyEmail, login, isLoading, error, isAuthenticated, clearError } = useAuth();

  // Fetch invite details if invite token is in URL
  const fetchInviteDetails = useCallback(async (token) => {
    setInviteLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await axios.get(`${API_BASE_URL}/contractors/invite/${token}`);
      if (response.data.success) {
        setInviteData(response.data.data);
        setInviteToken(token);
        // Pre-fill form with invite data
        const names = (response.data.data.name || '').split(' ');
        setFormData(prev => ({
          ...prev,
          role: 'contractor',
          email: response.data.data.email || prev.email,
          first_name: names[0] || prev.first_name,
          last_name: names.slice(1).join(' ') || prev.last_name
        }));
        // Skip role selection step
        setActiveStep(1);
      }
    } catch (err) {
      console.error('Invalid or expired invite token');
    } finally {
      setInviteLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = searchParams.get('invite');
    if (token && token.length === 64) {
      fetchInviteDetails(token);
    }
  }, [searchParams, fetchInviteDetails]);

  // Load per-role signup open/closed state so we can tell a user a role is
  // paused the moment they pick it — before they fill anything in.
  useEffect(() => {
    let active = true;
    api.get('/auth/signup-status')
      .then((res) => {
        if (!active) return;
        const map = {};
        (res.data?.data || []).forEach((r) => { map[r.role] = { isOpen: r.isOpen, pausedMessage: r.pausedMessage }; });
        setSignupStatus(map);
      })
      .catch(() => { /* fail open — don't block signup UI */ });
    return () => { active = false; };
  }, []);

  // Pre-select role and skip the role-selection step when arriving with ?role=
  // (e.g. from the homepage "Contractors"/"Verifiers" nav via login).
  useEffect(() => {
    if (searchParams.get('invite')) return; // invite flow sets role + step itself
    const role = searchParams.get('role');
    const allowed = ['client', 'contractor', 'verifier', ...(features.listings ? ['realtor', 'conveyancer'] : [])];
    if (allowed.includes(role)) {
      setFormData(prev => ({ ...prev, role }));
      setActiveStep(1);
    }
  }, [searchParams, features.listings]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (error) {
      clearError();
    }
  }, []);

  const validateField = (name, value) => {
    const errors = { ...validationErrors };
    
    switch (name) {
      case 'role':
        if (!value) errors[name] = 'Please select your role';
        else delete errors[name];
        break;
      case 'first_name':
      case 'last_name':
        if (!value.trim()) errors[name] = `${name.replace('_', ' ')} is required`;
        else if (value.trim().length < 2) errors[name] = `Must be at least 2 characters`;
        else delete errors[name];
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value) errors[name] = 'Email is required';
        else if (!emailRegex.test(value)) errors[name] = 'Please enter a valid email';
        else delete errors[name];
        break;
      case 'phone':
        // Optional at signup — collected in the complete-profile flow.
        // Validate the format only when something was typed.
        if (!value || !value.trim()) delete errors[name];
        else if (!isCompletePhone(value)) errors[name] = 'Check the number — pick your country and type it without the leading 0';
        else delete errors[name];
        break;
      case 'country_of_residence':
        // Optional at registration — can be added later from profile
        delete errors[name];
        break;
      case 'city':
        // Optional at registration — can be added later from profile
        delete errors[name];
        break;
      case 'province':
      case 'district':
        // Moved to the complete-profile flow (onboarding Basic Info) — never
        // required at signup.
        delete errors[name];
        break;
      case 'password':
        if (!value) errors[name] = 'Password is required';
        else if (value.length < 8) errors[name] = 'Minimum 8 characters';
        else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) errors[name] = 'Need uppercase, lowercase & number';
        else delete errors[name];
        break;
      case 'confirmPassword':
        if (!value) errors[name] = 'Please confirm password';
        else if (value !== formData.password) errors[name] = 'Passwords do not match';
        else delete errors[name];
        break;
      case 'accepted_terms':
        if (!value) errors[name] = 'You must accept the Terms of Service and Privacy Policy';
        else delete errors[name];
        break;
      default:
        break;
    }

    setValidationErrors(errors);
    return !errors[name];
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setFormData({ ...formData, [name]: fieldValue });
    validateField(name, fieldValue);
    if (error) clearError();
  };

  const handleRoleSelect = (role) => {
    setFormData({ ...formData, role });
    validateField('role', role);
    // If sign-ups for this role are paused, show the message immediately and
    // do not let the user proceed to fill in their details.
    const status = signupStatus[role];
    if (status && status.isOpen === false) {
      setRoleClosedMessage(status.pausedMessage || 'New sign-ups for this role are temporarily paused. Please check back soon.');
      return;
    }
    setRoleClosedMessage('');
    setTimeout(() => setActiveStep(1), 300);
  };

  const validateCurrentStep = () => {
    let isValid = true;
    switch (activeStep) {
      case 0:
        isValid = validateField('role', formData.role);
        break;
      case 1:
        isValid = validateField('first_name', formData.first_name) && validateField('last_name', formData.last_name);
        if (formData.account_type === 'company' && !formData.company_name.trim()) {
          setValidationErrors(prev => ({ ...prev, company_name: 'Company name is required' }));
          isValid = false;
        }
        break;
      case 2:
        isValid = validateField('email', formData.email);
        isValid = validateField('phone', formData.phone) && isValid;
        break;
      case 3:
        isValid = validateField('password', formData.password) && validateField('confirmPassword', formData.confirmPassword);
        isValid = validateField('accepted_terms', formData.accepted_terms) && isValid;
        break;
      default:
        break;
    }
    return isValid;
  };

  const handleNext = () => {
    // Don't let a user advance past role selection when that role is paused.
    const status = signupStatus[formData.role];
    if (activeStep === 0 && status && status.isOpen === false) {
      setRoleClosedMessage(status.pausedMessage || 'New sign-ups for this role are temporarily paused. Please check back soon.');
      return;
    }
    if (validateCurrentStep()) {
      if (activeStep === 3) handleSubmit();
      else setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSubmit = async () => {
    setSubmitError('');
    const { confirmPassword, accepted_terms, first_name, last_name, phone, country_of_residence, city, province, district, role, account_type, company_name, registration_number, ...rest } = formData;
    const userData = {
      ...rest,
      firstName: first_name,
      lastName: last_name,
      phoneNumber: phone || undefined,
      role: role,
      // For clients, use diaspora_location for country of residence
      ...(role === 'client' && country_of_residence && { diasporaLocation: country_of_residence }),
      // City for Client/Project Owner roles only
      // Merchants will provide city in their business profile physical address
      ...((role === 'client' || role === 'project_manager') && city && { city }),
      // Structured geography (required for contractors/verifiers).
      ...(province && { province }),
      ...(district && { district }),
      // Company registration fields
      ...(account_type && { accountType: account_type }),
      ...(company_name && { companyName: company_name }),
      ...(registration_number && { registrationNumber: registration_number }),
      // Invite token for contractor invitations
      ...(inviteToken && { inviteToken })
    };

    // Get reCAPTCHA token (returns null if not configured)
    const recaptchaToken = await getRecaptchaToken('register');

    const result = await register(userData, recaptchaToken);
    
    if (result.success) {
      // Store userId and email for verification page (used by EmailVerificationFlow on page reload)
      if (result.user?.id) {
        localStorage.setItem('pending_verification_userId', result.user.id);
      }
      localStorage.setItem('pending_verification_email', formData.email);
      // Keep ?next= across the /verify-email step (the URL loses it), so the
      // guest project wizard gets its user back on Review after verification.
      const nextParam = searchParams.get('next');
      if (nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//')) {
        localStorage.setItem('pending_verification_next', nextParam);
      }

      // Store invite details for post-verification redirect
      if (result.inviteDetails) {
        localStorage.setItem('invite_project_id', result.inviteDetails.projectId);
        localStorage.setItem('invite_quote_request_id', result.inviteDetails.quoteRequestId);
      }

      setRegisteredUser(result.user);
      setShowEmailVerification(true);
      setCodeExpirySeconds(600); // 10 minutes

      // Warn user if verification email wasn't delivered
      if (!result.verificationEmailSent) {
        setEmailSentWarning(true);
      }
    } else {
      // Surface the failure (e.g. sign-ups paused, duplicate email) instead of
      // silently doing nothing.
      setSubmitError(result.error || 'Registration could not be completed. Please try again.');
    }
  };

  // Code expiry countdown timer
  useEffect(() => {
    if (codeExpirySeconds <= 0) return;
    const timer = setInterval(() => {
      setCodeExpirySeconds((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [codeExpirySeconds]);

  const handleResendCode = async () => {
    const emailToResend = registeredUser?.email || localStorage.getItem('pending_verification_email');
    if (resendCooldown > 0 || !emailToResend) return;

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await axios.post(`${API_BASE_URL}/auth/resend-verification`, {
        email: emailToResend
      });
      setResendSuccess('A new code has been sent to your email.');
      setTimeout(() => setResendSuccess(''), 5000);
      setCodeExpirySeconds(600); // Reset 10-minute countdown
      setVerificationCode(''); // Clear old code input
      verificationCodeRef.current = '';
    } catch (err) {
      // Show generic message even on error — endpoint returns success either way
      setResendSuccess('A new code has been sent if your account is unverified.');
      setTimeout(() => setResendSuccess(''), 5000);
    }

    // Start 60-second cooldown
    setResendCooldown(60);
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    setVerificationError('');

    // Use ref for the freshest value (avoids stale closure on auto-submit)
    const code = verificationCodeRef.current || verificationCode;
    if (!code || code.length !== 6) {
      setVerificationError('Please enter a valid 6-digit code');
      return;
    }

    const userIdToVerify = registeredUser?.id || localStorage.getItem('pending_verification_userId');
    if (!userIdToVerify) {
      setVerificationError('Session expired. Please use the verification link from your email or go to the login page.');
      return;
    }

    const result = await verifyEmail(userIdToVerify, code);

    if (result.success) {
      setShowSuccessMessage(true);
      
      // Clear sensitive data
      localStorage.removeItem('pending_verification_userId');
      localStorage.removeItem('pending_verification_email');

      // Check for invite-based redirect
      const inviteProjectId = localStorage.getItem('invite_project_id');
      const inviteQuoteId = localStorage.getItem('invite_quote_request_id');

      // Redirect based on role after auto-login
      setTimeout(() => {
        // Clean up invite data
        localStorage.removeItem('invite_project_id');
        localStorage.removeItem('invite_quote_request_id');

        if (result.user) {
          // If contractor came via invite, go to their received quotes
          if (inviteProjectId && result.user.role === 'contractor') {
            navigate('/received-quotes');
            return;
          }

          // User is now logged in, redirect based on role
          const role = result.user.role || registeredUser?.role;

          // ?next= (app-relative only): e.g. the guest project wizard sends
          // people here mid-flow and wants them back on Review afterwards.
          const nextPath = searchParams.get('next') || localStorage.getItem('pending_verification_next');
          localStorage.removeItem('pending_verification_next');
          if (nextPath && nextPath.startsWith('/') && !nextPath.startsWith('//')) {
            navigate(nextPath);
            return;
          }

          // Role-specific onboarding redirects
          const onboardingRoutes = {
            client: '/dashboard', // Clients land on the dashboard; "New Project" opens the wizard
            contractor: '/contractor/apply', // Full-page application wizard
            verifier: '/verifier/apply', // Full-page application (same gate as contractors)
            quality_inspector: '/verifier/apply',
            professional_verifier: '/verifier/apply',
            project_manager: '/project-manager/onboarding',
            merchant: '/merchant/onboarding'
          };

          navigate(onboardingRoutes[role] || '/dashboard');
        } else {
          // Fallback to login if auto-login didn't work
          navigate('/login', { 
            state: { 
              message: 'Email verified successfully! Please log in to continue.',
              email: pendingEmail
            }
          });
        }
      }, 2000);
    } else {
      setVerificationError(result.error || 'Verification failed. Please try again.');
    }
  };

  // Shared image panel component for split layout
  const ImagePanel = ({ stepIndex = activeStep }) => {
    const img = (stepIndex === 1 && ROLE_DETAIL_IMAGES[formData.role])
      || STEP_IMAGES[Math.min(stepIndex, STEP_IMAGES.length - 1)];
    return (
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        position: 'relative',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        minHeight: '100vh',
        width: '45%',
        flexShrink: 0,
        overflow: 'hidden',
      }}>
        <Box
          component="img"
          src={img.src}
          alt=""
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {/* Dark warm scrim — NOT teal */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(15,12,8,0.85) 0%, rgba(15,12,8,0.3) 50%, rgba(15,12,8,0.1) 100%)',
        }} />
        <Box sx={{ position: 'relative', p: 5, zIndex: 1 }}>
          <Typography sx={{
            color: 'rgba(255,255,255,0.5)',
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            mb: 1,
          }}>
            {img.stat}
          </Typography>
          <Typography sx={{
            color: 'white',
            fontWeight: 700,
            fontSize: '1.5rem',
            lineHeight: 1.2,
          }}>
            {img.quote}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Show Email Verification
  const pendingEmail = registeredUser?.email || localStorage.getItem('pending_verification_email');
  const pendingUserId = registeredUser?.id || localStorage.getItem('pending_verification_userId');
  if (showEmailVerification && (registeredUser || pendingEmail)) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#faf9f7' }}>
        <ImagePanel stepIndex={3} />
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 3, md: 5 } }}>
          <Box sx={{ maxWidth: 480, width: '100%' }}>
          <Box textAlign="center" mb={3}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 64, height: 64, borderRadius: '50%',
              bgcolor: `${brand.primary}14`, mb: 2
            }}>
              <MarkEmailRead sx={{ fontSize: 32, color: brand.primary }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }} gutterBottom>
              Verify Your Email
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {emailSentWarning
                ? 'Enter the 6-digit verification code for'
                : "We've sent a 6-digit code to"}
            </Typography>
            <Typography variant="subtitle1" color="primary" fontWeight={600} sx={{ mt: 1 }}>
              {pendingEmail}
            </Typography>
          </Box>

          {showSuccessMessage && (
            <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>
              Email verified! Setting up your account...
            </Alert>
          )}

          {verificationError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {verificationError}
            </Alert>
          )}

          {emailSentWarning && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              The verification email may not have been delivered. Please click "Resend" below to request a new code.
            </Alert>
          )}

          <form onSubmit={handleVerificationSubmit}>
            {/* 6 individual digit boxes */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1, sm: 1.5 }, mb: 3 }}>
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  ref={(el) => { digitRefs.current[index] = el; }}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={verificationCode[index] || ''}
                  disabled={isLoading}
                  style={{
                    width: 48, height: 56,
                    textAlign: 'center',
                    fontSize: '24px',
                    fontWeight: 700,
                    border: `2px solid ${verificationCode[index] ? brand.primary : '#d0d0d0'}`,
                    borderRadius: 10,
                    outline: 'none',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    fontFamily: 'monospace',
                    background: isLoading ? '#f5f5f5' : '#fff',
                    caretColor: brand.primary,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = brand.primary;
                    e.target.style.boxShadow = `0 0 0 3px ${brand.primary}22`;
                    e.target.select();
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = verificationCode[index] ? brand.primary : '#d0d0d0';
                    e.target.style.boxShadow = 'none';
                  }}
                  onChange={(e) => {
                    const digit = e.target.value.replace(/\D/g, '').slice(-1);
                    if (!digit && !e.target.value) return; // ignore empty
                    const newCode = verificationCode.split('');
                    newCode[index] = digit;
                    const joined = newCode.join('').slice(0, 6);
                    setVerificationCode(joined);
                    verificationCodeRef.current = joined;
                    setVerificationError('');
                    // Auto-focus next box
                    if (digit && index < 5) {
                      digitRefs.current[index + 1]?.focus();
                    }
                    // Auto-submit when all 6 digits entered
                    if (joined.length === 6 && index === 5) {
                      setTimeout(() => {
                        const form = digitRefs.current[0]?.closest('form');
                        if (form) form.requestSubmit();
                      }, 100);
                    }
                  }}
                  onKeyDown={(e) => {
                    // Backspace: clear current and move back
                    if (e.key === 'Backspace') {
                      e.preventDefault();
                      const newCode = verificationCode.split('');
                      if (newCode[index]) {
                        newCode[index] = '';
                        const joined = newCode.join('');
                        setVerificationCode(joined);
                        verificationCodeRef.current = joined;
                      } else if (index > 0) {
                        newCode[index - 1] = '';
                        const joined = newCode.join('');
                        setVerificationCode(joined);
                        verificationCodeRef.current = joined;
                        digitRefs.current[index - 1]?.focus();
                      }
                    }
                    // Arrow keys
                    if (e.key === 'ArrowLeft' && index > 0) digitRefs.current[index - 1]?.focus();
                    if (e.key === 'ArrowRight' && index < 5) digitRefs.current[index + 1]?.focus();
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                    if (pasted) {
                      setVerificationCode(pasted);
                      verificationCodeRef.current = pasted;
                      setVerificationError('');
                      const focusIndex = Math.min(pasted.length, 5);
                      digitRefs.current[focusIndex]?.focus();
                      // Auto-submit if 6 digits pasted
                      if (pasted.length === 6) {
                        setTimeout(() => {
                          const form = digitRefs.current[0]?.closest('form');
                          if (form) form.requestSubmit();
                        }, 100);
                      }
                    }
                  }}
                />
              ))}
            </Box>

            {/* Code expiry countdown */}
            {codeExpirySeconds > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>
                Code expires in {Math.floor(codeExpirySeconds / 60)}:{String(codeExpirySeconds % 60).padStart(2, '0')}
              </Typography>
            )}
            {codeExpirySeconds === 0 && showEmailVerification && (
              <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>
                Code expired. Please request a new one below.
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading || verificationCode.length !== 6}
              sx={{ mb: 2 }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Continue'}
            </Button>

            {resendSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {resendSuccess}
              </Alert>
            )}

            {/* Check spam hint */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1.5 }}>
              Can't find the email? Check your spam or junk folder.
            </Typography>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Didn't receive the code?{' '}
                <Button
                  variant="text"
                  size="small"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || isLoading}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                </Button>
              </Typography>
            </Box>
          </form>
          </Box>
        </Box>
      </Box>
    );
  }

  // Onboarding screens
  if (showDiasporaOnboarding) {
    return <DiasporaOnboarding onComplete={() => navigate('/dashboard')} onBack={() => { setShowDiasporaOnboarding(false); navigate('/dashboard'); }} />;
  }
  // Phase 2: Conversational AI Onboarding (Minimum Viable Profile)
  // Phase 2: Conversational AI Onboarding for Service Providers
  // Contractors and verifier-type roles are routed to full-page applications — skip inline modal for them.
  if (showConversationalOnboarding && formData.role !== 'contractor' && formData.role !== 'verifier' && formData.role !== 'quality_inspector' && formData.role !== 'professional_verifier') {
    return <UnifiedOnboardingModal userRole={formData.role} />;
  }

  if (showProjectManagerOnboarding) {
    return <ProjectManagerOnboarding onComplete={async (formData) => {
      // Save project manager profile with identity document
      try {
        const token = localStorage.getItem('eqaya_token');
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        
        // Save identity document if provided
        if (formData.identityDocumentUrl) {
          await axios.post(
            `${API_BASE_URL}/users/documents`,
            {
              documentType: formData.identityDocumentType === 'national_id' ? 'id_card' :
                           formData.identityDocumentType === 'license' ? 'license' : 'passport',
              documentName: formData.identityDocumentType === 'national_id' ? 'National ID' :
                           formData.identityDocumentType === 'license' ? 'Professional License' : 'Passport',
              fileUrl: formData.identityDocumentUrl
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        
        // Update user profile with onboarding data
        await axios.put(
          `${API_BASE_URL}/users/profile`,
          {
            ...formData,
            identityDocumentUrl: formData.identityDocumentUrl
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        navigate('/dashboard');
      } catch (error) {
        console.error('Error saving project manager profile:', error);
        navigate('/dashboard'); // Navigate anyway
      }
    }} />;
  }
  if (showVerifierOnboarding) {
    return <UnifiedOnboardingModal userRole="verifier" />;
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <ImprovedRoleSelector
              selectedRole={formData.role}
              onRoleSelect={handleRoleSelect}
            />
            {roleClosedMessage && (
              <Alert severity="info" sx={{ mt: 3, maxWidth: 560, mx: 'auto' }}>
                {roleClosedMessage}
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Box textAlign="center" mb={3}>
              <Typography variant="h6" gutterBottom>
                {['contractor', 'verifier', 'merchant', 'realtor', 'conveyancer'].includes(formData.role) 
                  ? "Tell us about yourself" 
                  : "What's your name?"}
              </Typography>
            </Box>

            {/* Account Type toggle for contractor/verifier/merchant */}
            {['contractor', 'verifier', 'merchant', 'realtor', 'conveyancer'].includes(formData.role) && (
              <Box sx={{ mb: 2, p: 2, bgcolor: bg.subtle, borderRadius: 2, border: `1px solid ${border.light}` }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1 }}>
                    Are you registering as an individual or a company?
                  </FormLabel>
                  <RadioGroup
                    row
                    name="account_type"
                    value={formData.account_type}
                    onChange={handleChange}
                  >
                    <FormControlLabel 
                      value="individual" 
                      control={<Radio size="small" />} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Person fontSize="small" /> Individual
                        </Box>
                      }
                    />
                    <FormControlLabel 
                      value="company" 
                      control={<Radio size="small" />} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Business fontSize="small" /> Company
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}

            {/* Company fields when registering as a company */}
            {formData.account_type === 'company' && (
              <>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  margin="normal"
                  error={!!validationErrors.company_name}
                  helperText={validationErrors.company_name}
                  InputProps={{
                    startAdornment: <Business sx={{ mr: 1, color: 'action.active' }} />
                  }}
                />
                <TextField
                  fullWidth
                  label="Company Registration Number (optional)"
                  name="registration_number"
                  value={formData.registration_number}
                  onChange={handleChange}
                  margin="normal"
                  placeholder="e.g. CR/2024/12345"
                  helperText="Tax or company registration number"
                />
              </>
            )}

            {/* Contact person name (always required) */}
            {formData.account_type === 'company' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 0 }}>
                Contact person details:
              </Typography>
            )}

            <TextField
              fullWidth
              label={formData.account_type === 'company' ? "Contact Person First Name" : "First Name"}
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              margin="normal"
              error={!!validationErrors.first_name}
              helperText={validationErrors.first_name}
              InputProps={{
                startAdornment: <Person sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
            <TextField
              fullWidth
              label={formData.account_type === 'company' ? "Contact Person Last Name" : "Last Name"}
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              margin="normal"
              error={!!validationErrors.last_name}
              helperText={validationErrors.last_name}
              InputProps={{
                startAdornment: <Person sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Box textAlign="center" mb={3}>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
            </Box>

            <Box>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
                error={!!validationErrors.email}
                helperText={validationErrors.email}
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
              
              <PhoneField
                label="WhatsApp / Phone Number (optional)"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                defaultIso={formData.country_of_residence || 'ZW'}
                margin="normal"
                error={!!validationErrors.phone}
                helperText={validationErrors.phone || 'Optional now, add it any time — with WhatsApp on your profile, contractors and clients can reach you through Eqaya without logging in'}
                InputProps={{
                  startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />
                }}
              />

              {/* Province/district moved to the complete-profile flow
                  (onboarding Basic Info) — signup stays name + email + password. */}

              {/* Additional fields for Client/Project Owner */}
              {(formData.role === 'client' || formData.role === 'project_manager') && (
                <>
                  <FormControl fullWidth error={!!validationErrors.country_of_residence} sx={{ mt: 2 }}>
                    <InputLabel>Country of Residence (optional)</InputLabel>
                    <Select
                      name="country_of_residence"
                      value={formData.country_of_residence}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({ ...formData, country_of_residence: value });
                        validateField('country_of_residence', value);
                        if (error) clearError();
                      }}
                      label="Country of Residence (optional)"
                    >
                      <MenuItem value="ZW">Zimbabwe</MenuItem>
                      <MenuItem value="US">United States</MenuItem>
                      <MenuItem value="GB">United Kingdom</MenuItem>
                      <MenuItem value="CA">Canada</MenuItem>
                      <MenuItem value="AU">Australia</MenuItem>
                      <MenuItem value="ZA">South Africa</MenuItem>
                      <MenuItem value="KE">Kenya</MenuItem>
                      <MenuItem value="TZ">Tanzania</MenuItem>
                      <MenuItem value="UG">Uganda</MenuItem>
                      <MenuItem value="NG">Nigeria</MenuItem>
                      <MenuItem value="GH">Ghana</MenuItem>
                      <MenuItem value="BW">Botswana</MenuItem>
                      <MenuItem value="ZM">Zambia</MenuItem>
                      <MenuItem value="MW">Malawi</MenuItem>
                      <MenuItem value="MZ">Mozambique</MenuItem>
                      <MenuItem value="NA">Namibia</MenuItem>
                      <MenuItem value="IE">Ireland</MenuItem>
                      <MenuItem value="NZ">New Zealand</MenuItem>
                      <MenuItem value="DE">Germany</MenuItem>
                      <MenuItem value="FR">France</MenuItem>
                      <MenuItem value="IT">Italy</MenuItem>
                      <MenuItem value="ES">Spain</MenuItem>
                      <MenuItem value="NL">Netherlands</MenuItem>
                      <MenuItem value="BE">Belgium</MenuItem>
                      <MenuItem value="CH">Switzerland</MenuItem>
                      <MenuItem value="AT">Austria</MenuItem>
                      <MenuItem value="SE">Sweden</MenuItem>
                      <MenuItem value="NO">Norway</MenuItem>
                      <MenuItem value="DK">Denmark</MenuItem>
                      <MenuItem value="FI">Finland</MenuItem>
                      <MenuItem value="PT">Portugal</MenuItem>
                      <MenuItem value="GR">Greece</MenuItem>
                      <MenuItem value="PL">Poland</MenuItem>
                      <MenuItem value="CZ">Czech Republic</MenuItem>
                      <MenuItem value="HU">Hungary</MenuItem>
                      <MenuItem value="RO">Romania</MenuItem>
                      <MenuItem value="BG">Bulgaria</MenuItem>
                      <MenuItem value="HR">Croatia</MenuItem>
                      <MenuItem value="SI">Slovenia</MenuItem>
                      <MenuItem value="SK">Slovakia</MenuItem>
                      <MenuItem value="EE">Estonia</MenuItem>
                      <MenuItem value="LV">Latvia</MenuItem>
                      <MenuItem value="LT">Lithuania</MenuItem>
                      <MenuItem value="LU">Luxembourg</MenuItem>
                      <MenuItem value="MT">Malta</MenuItem>
                      <MenuItem value="CY">Cyprus</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  {validationErrors.country_of_residence && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {validationErrors.country_of_residence}
                    </Typography>
                  )}

                  <TextField
                    fullWidth
                    label="City (optional)"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    margin="normal"
                    error={!!validationErrors.city}
                    helperText={validationErrors.city || 'Helps us match you with local contractors'}
                    InputProps={{
                      startAdornment: <LocationOn sx={{ mr: 1, color: 'action.active' }} />
                    }}
                  />
                </>
              )}

            </Box>
          </Box>
        );

      case 3:
        const selectedRole = ROLE_OPTIONS.find(r => r.value === formData.role);
        return (
          <Box>
            <Box textAlign="center" mb={3}>
              <Typography variant="h6" gutterBottom>
                Create your password
              </Typography>
            </Box>

            {/* Account Summary */}
            <Box 
              sx={{ 
                p: 2, 
                mb: 3, 
                borderRadius: 1,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Role:</strong> {selectedRole?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Name:</strong> {formData.first_name} {formData.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Email:</strong> {formData.email}
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              error={!!validationErrors.password}
              helperText={validationErrors.password || 'Min 8 chars with uppercase, lowercase & number'}
              InputProps={{
                startAdornment: <Lock sx={{ mr: 1, color: 'action.active' }} />,
                endAdornment: (
                  <Button onClick={() => setShowPassword(!showPassword)} size="small">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </Button>
                )
              }}
            />

            {/* Password strength indicator — aligned with backend requirements */}
            {formData.password && (() => {
              const p = formData.password;
              const hasUpper = /[A-Z]/.test(p);
              const hasLower = /[a-z]/.test(p);
              const hasNumber = /\d/.test(p);
              const long = p.length >= 8;
              const extraLong = p.length >= 12;
              const hasSpecial = /[^a-zA-Z\d]/.test(p);
              // Required: length >= 8, uppercase, lowercase, digit
              const requiredMet = [long, hasUpper, hasLower, hasNumber].filter(Boolean).length;
              // Bonus: special char, length >= 12
              const bonus = [hasSpecial, extraLong].filter(Boolean).length;
              // Score: 0-4 required, then bonus brings to 5-6
              const score = requiredMet + (requiredMet === 4 ? bonus : 0);
              const levels = ['', 'Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Strong'];
              const colors = ['', status.error, status.error, status.warning, status.success, status.success, status.success];
              return (
                <Box sx={{ mt: 1, mb: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((score / 5) * 100, 100)}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': { bgcolor: colors[score], borderRadius: 3 }
                    }}
                  />
                  <Typography variant="caption" sx={{ color: colors[score], fontWeight: 600 }}>
                    {levels[score]}
                  </Typography>
                </Box>
              );
            })()}
            
            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              margin="normal"
              error={!!validationErrors.confirmPassword}
              helperText={validationErrors.confirmPassword}
              InputProps={{
                startAdornment: <Lock sx={{ mr: 1, color: 'action.active' }} />
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="accepted_terms"
                  checked={formData.accepted_terms}
                  onChange={handleChange}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: brand.primary }}>
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: brand.primary }}>
                    Privacy Policy
                  </a>
                </Typography>
              }
              sx={{ mt: 2, alignItems: 'flex-start' }}
            />
            {validationErrors.accepted_terms && (
              <Typography variant="caption" color="error" sx={{ ml: 4 }}>
                {validationErrors.accepted_terms}
              </Typography>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#faf9f7' }}>
      {/* Left: Image panel (desktop only) */}
      <ImagePanel />

      {/* Right: Form panel */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        p: { xs: 3, sm: 4, md: 5 },
        overflowY: 'auto',
      }}>
        <Box sx={{ maxWidth: 520, width: '100%', mx: 'auto' }}>
          {/* Mobile hero image (shown when side panel is hidden) */}
          {(() => {
            const img = (activeStep === 1 && ROLE_DETAIL_IMAGES[formData.role])
              || STEP_IMAGES[Math.min(activeStep, STEP_IMAGES.length - 1)];
            return (
              <Box sx={{
                display: { xs: 'block', md: 'none' },
                position: 'relative',
                height: 160,
                borderRadius: 3,
                overflow: 'hidden',
                mb: 3,
              }}>
                <Box
                  component="img"
                  src={img.src}
                  alt=""
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <Box sx={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(to top, rgba(15,12,8,0.8) 0%, rgba(15,12,8,0.2) 100%)',
                }} />
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, p: 2, zIndex: 1 }}>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>
                    {img.quote}
                  </Typography>
                </Box>
              </Box>
            );
          })()}
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{
              color: brand.primary,
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              mb: 1,
            }}>
              Step {activeStep + 1} of {steps.length}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: text.primary, mb: 0.5 }}>
              {steps[activeStep]}
            </Typography>
            <Typography sx={{ color: text.secondary, fontSize: '0.9rem' }}>
              Create your free Eqaya account
            </Typography>
          </Box>

          {/* Step indicator dots */}
          <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
            {steps.map((_, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  height: 3,
                  borderRadius: 2,
                  bgcolor: i <= activeStep ? brand.primary : border.light,
                  transition: 'background-color 0.3s cubic-bezier(0.4,0,0.2,1)',
                }}
              />
            ))}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {inviteData && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={false}>
              <Typography variant="subtitle2" fontWeight="bold">
                You've been invited by {inviteData.inviterName}
              </Typography>
              <Typography variant="body2">
                Project: <strong>{inviteData.projectTitle}</strong>
                {inviteData.projectLocation && ` — ${inviteData.projectLocation}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Complete registration to review the project and submit your quote.
              </Typography>
            </Alert>
          )}

          {inviteLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 1 }}>Loading invite details...</Typography>
            </Box>
          )}

          {/* Step Content */}
          <Box sx={{ minHeight: 280 }}>
            {renderStepContent()}
          </Box>

          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSubmitError('')}>
              {submitError}
            </Alert>
          )}

          {/* Navigation */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 4,
            pt: 3,
            borderTop: `1px solid ${border.light}`,
          }}>
            <Button
              disabled={activeStep === 0 || isLoading}
              onClick={handleBack}
              startIcon={<ArrowBack />}
              aria-label="Go to previous step"
              sx={{ color: text.secondary }}
            >
              Back
            </Button>

            <Button
              variant="contained"
              size="large"
              onClick={handleNext}
              disabled={isLoading || (activeStep === 0 && !formData.role)}
              endIcon={activeStep === 3 ? (isLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />) : <ArrowForward />}
              aria-label={activeStep === 3 ? 'Create account' : 'Continue to next step'}
              sx={{
                minWidth: 160,
                bgcolor: activeStep === 3 ? brand.accent : brand.primary,
                fontWeight: 700,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: activeStep === 3 ? brand.accentLight : brand.primaryDark,
                  ...(activeStep === 3 && { color: brand.primaryDeep }),
                  transform: 'translateY(-1px)',
                },
                transition: 'transform 0.2s cubic-bezier(0.34,1.56,0.64,1), background-color 0.2s',
              }}
            >
              {activeStep === 3 ? (isLoading ? 'Creating...' : 'Create Account') : 'Continue'}
            </Button>
          </Box>

          {/* Login link */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" sx={{ color: text.secondary }}>
              Already have an account?{' '}
              <Link
                to="/login"
                style={{ color: brand.primary, fontWeight: 600, textDecoration: 'none' }}
              >
                Sign in
              </Link>
            </Typography>
          </Box>

          {/* Trust badges */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            mt: 4,
            pt: 3,
            borderTop: `1px solid ${border.light}`,
          }}>
            {[
              { icon: '🔒', label: 'Secure Wallet' },
              { icon: '✓', label: 'Verified Contractors' },
              { icon: '📍', label: 'GPS Evidence' },
            ].map((badge) => (
              <Box key={badge.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.75rem' }}>{badge.icon}</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: text.tertiary, fontWeight: 500 }}>
                  {badge.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RegisterPage;
